import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TemplatePreviewDialog } from "@/components/TemplatePreviewDialog";
import { FRAMEWORKS, CORE_FRAMEWORKS, AI_FRAMEWORKS } from "@/data/testimonials";
import { trackEvent } from "@/lib/api";
import {
    ArrowRight, Check, FileDown, FileText,
    MessageSquare, NotebookPen, Send, Users,
} from "lucide-react";

const COLLECT_ITEMS = [
    { label: "Access logs", desc: "who logged in, when, and from where" },
    { label: "Security policies", desc: "password, clean desk, acceptable use, and more" },
    { label: "System configurations", desc: "proof your controls are actually turned on" },
    { label: "Training records", desc: "evidence your team completed required security training" },
    { label: "Vendor agreements", desc: "contracts and security assessments for third parties" },
    { label: "Incident records", desc: "documented responses to any security events" },
];

const FEATURES = [
    {
        icon: Users,
        title: "Multi-tenant Portal",
        body: "Every client gets an isolated workspace with their own login. Notes, files, tasks and evidence never cross tenant lines.",
        chip: "Isolation",
    },
    {
        icon: Send,
        title: "Zero-Touch Onboarding",
        body: "Share your intake link — prospects enter their company, contact and frameworks, and a ready-to-work workspace appears in your dashboard with your baselines applied.",
        chip: "Automation",
    },
    {
        icon: NotebookPen,
        title: "Compliance Templates",
        body: "Save your firm's baseline notes per control once. Every new client starts with a strong draft — not a blank page.",
        chip: "Time-saver",
    },
    {
        icon: FileDown,
        title: "Branded PDF Exports",
        body: "One-click audit reports with your logo, firm name and executive opinion on the cover. Every generated PDF is versioned and archived per client, so you can re-share the exact snapshot without regenerating.",
        chip: "Deliverable",
    },
    {
        icon: MessageSquare,
        title: "Client communication and reminders",
        body: "Direct messaging keeps the audit conversation organized — no more chasing evidence through email. Automatic nudges fire when a control sits in-progress for 30 days with no evidence, so nothing slips.",
        chip: "Chase-free",
    },
    {
        icon: FileText,
        title: "Policy Template Library",
        body: "Curated policy templates for SOC 2 and ISO 27001 — Clean Desk, BYOD, Incident Response — that you share with a click to jumpstart client compliance.",
        chip: "Jumpstart",
    },
];

export const FrameworksStrip = () => (
    <section
        className="max-w-7xl mx-auto px-5 sm:px-10 py-8 border-t border-border/60"
        data-testid="frameworks-strip"
    >
        <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="mono-label">Coverage across</div>
            <div className="flex flex-wrap items-center gap-3">
                {CORE_FRAMEWORKS.map((f) => (
                    <Badge
                        key={f}
                        variant="outline"
                        className="rounded-full border-border/70 font-mono text-[11px] tracking-widest uppercase px-4 py-1.5 text-foreground"
                    >
                        {f}
                    </Badge>
                ))}
            </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-6" data-testid="ai-frameworks-row">
            <div className="mono-label text-muted-foreground">Also covers (AI governance)</div>
            <div className="flex flex-wrap items-center gap-3">
                {AI_FRAMEWORKS.map((f) => (
                    <Badge
                        key={f}
                        variant="outline"
                        className="rounded-full border-border/70 font-mono text-[11px] tracking-widest uppercase px-4 py-1.5 text-muted-foreground"
                    >
                        {f}
                    </Badge>
                ))}
            </div>
        </div>
    </section>
);

export const FeaturesSection = () => {
    const [previewOpen, setPreviewOpen] = useState(false);
    return (
        <section id="templates" className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 scroll-mt-24" data-testid="features-section">
            <div className="max-w-3xl mb-14">
                <div className="mono-label mb-3">Core features</div>
                <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                    Every tool your practice needs.
                    <br />
                    Purpose-built for GRC.
                </h2>
                <p className="mt-6 text-muted-foreground max-w-xl">
                    AuditVanguard replaces the shared Google Drive, the tangled email
                    thread and the last-minute report scramble with one workspace
                    designed for compliance work.
                </p>
                <Button
                    variant="outline"
                    className="mt-6 rounded-md font-display border-foreground/20"
                    data-testid="template-preview-btn"
                    onClick={() => {
                        trackEvent("landing_cta_click", {
                            location: "features",
                            label: "template_preview",
                        });
                        setPreviewOpen(true);
                    }}
                >
                    Preview The SOC 2 Template
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
            </div>

            <div className="mb-14" data-testid="evidence-collect-intro">
                <p className="text-base sm:text-lg font-medium">
                    Here is exactly what we collect and organize for you:
                </p>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 max-w-4xl">
                    {COLLECT_ITEMS.map((it) => (
                        <li key={it.label} className="flex items-start gap-3" data-testid={`collect-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <Check className="h-4 w-4 mt-1 shrink-0 text-muted-foreground" strokeWidth={2} aria-hidden="true" />
                            <p className="text-base leading-relaxed min-w-0">
                                <span className="font-medium">{it.label}</span>
                                <span className="text-muted-foreground" aria-hidden="true">{" — "}</span>
                                <span className="text-muted-foreground">{it.desc}</span>
                            </p>
                        </li>
                    ))}
                </ul>
                <p className="mt-4 text-sm text-muted-foreground" data-testid="collect-connectors-note">
                    Evidence arrives by upload or through live read-only connectors for Okta, AWS and GitHub.
                </p>
            </div>

            <div className="border-t border-border" data-testid="features-list">
                {FEATURES.map((f, i) => (
                    <div
                        key={f.title}
                        className="flex items-start gap-4 py-6 border-b border-border"
                        data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <span className="font-mono text-sm text-muted-foreground leading-none select-none mt-3 w-7 shrink-0" aria-hidden="true">
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="w-10 h-10 rounded-xl border border-border bg-card grid place-items-center shrink-0">
                            <f.icon className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0">
                            <div>
                                <Badge
                                    variant="outline"
                                    className="rounded-full border-border/70 text-xs font-mono tracking-widest uppercase whitespace-nowrap"
                                >
                                    {f.chip}
                                </Badge>
                            </div>
                            <h3 className="font-display text-xl font-semibold tracking-tight mt-2">
                                {f.title}
                            </h3>
                            <p className="text-base text-muted-foreground leading-relaxed mt-1.5 max-w-2xl">
                                {f.body}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
            <TemplatePreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} />
        </section>
    );
};
